import { useId } from 'react';
import type { TenantPoolCapacityDto, TenantPoolStatusDto } from '@shared/api-types';

// Region tenant-pool capacity (AAI-144). New tenants fill the registered pools
// in order; pools come from Bicep (tenantPoolCount) and are registered in the
// sql.tenantPools system setting. Every state is spelled out in text — colour
// only reinforces it (PRODUCT.md: never encode state in colour alone).

const MONO = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)';

/** The text label for one pool's state. */
function poolStateLabel(pool: TenantPoolStatusDto): string {
  if (pool.existsOnServer === false) return 'Missing on server';
  if (pool.databaseCount === null) return 'Unknown';
  if (!pool.acceptingNew) return 'Closed';
  return pool.eligible ? 'Accepting' : 'Full';
}

const STATE_COLOR: Record<string, string> = {
  Accepting: '#16a34a',
  Full: '#d97706',
  Closed: '#64748b',
  'Missing on server': '#dc2626',
  Unknown: '#64748b',
};

interface Props {
  capacity: TenantPoolCapacityDto | null;
  error: string | null;
}

export function TenantPoolCapacity({ capacity, error }: Props) {
  const titleId = useId();
  if (error) {
    return (
      <div style={panelStyle} data-testid="tenant-pools">
        <div style={{ color: '#dc2626', fontSize: 13 }}>Tenant pools: {error}</div>
      </div>
    );
  }
  if (!capacity) return null;

  const pct = capacity.capacityAccepting > 0
    ? Math.round((capacity.usedAccepting * 100) / capacity.capacityAccepting)
    : 100;

  return (
    <section style={panelStyle} data-testid="tenant-pools" aria-label="Tenant database pools">
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong id={titleId} style={{ fontSize: 13 }}>Tenant pools</strong>
        {capacity.liveDataAvailable ? (
          <span style={{ fontSize: 13 }}>
            {capacity.usedAccepting} of {capacity.capacityAccepting} databases ({pct}%) in pools accepting new tenants
          </span>
        ) : (
          <span style={{ fontSize: 13, color: '#64748b' }}>Live counts unavailable — the SQL server could not be read.</span>
        )}
        {capacity.liveDataAvailable && capacity.nearCapacity && (
          <span style={{ fontSize: 12, fontWeight: 600, color: '#d97706' }}>
            Near capacity — add the next pool
          </span>
        )}
      </div>

      {capacity.registryError && (
        <div style={{ color: '#dc2626', fontSize: 13, marginTop: 6 }}>
          sql.tenantPools is invalid: {capacity.registryError}
        </div>
      )}

      <table aria-labelledby={titleId} style={{ marginTop: 8, fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: '#64748b', textAlign: 'left' }}>
            <th style={cellStyle}>Pool</th>
            <th style={cellStyle}>Databases</th>
            <th style={cellStyle}>Soft / hard cap</th>
            <th style={cellStyle} title="Soft-deleted XXXDELETE- databases still using a slot">Reclaimable</th>
            <th style={cellStyle}>State</th>
          </tr>
        </thead>
        <tbody>
          {capacity.pools.map((pool) => {
            const label = poolStateLabel(pool);
            return (
              <tr key={pool.name}>
                <td style={{ ...cellStyle, fontFamily: MONO }}>{pool.name}</td>
                <td style={cellStyle}>{pool.databaseCount ?? '—'}</td>
                <td style={cellStyle}>{pool.softCap} / {pool.hardCap}</td>
                <td style={cellStyle}>{pool.reclaimableCount ?? '—'}</td>
                <td style={{ ...cellStyle, color: STATE_COLOR[label], fontWeight: 600 }}>{label}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {capacity.unregisteredServerPools.length > 0 && (
        <div style={{ fontSize: 12, marginTop: 6, color: '#d97706' }}>
          Not registered (takes no tenants):{' '}
          <span style={{ fontFamily: MONO }}>{capacity.unregisteredServerPools.join(', ')}</span>
          {' '}— add to <span style={{ fontFamily: MONO }}>sql.tenantPools</span> in System Settings.
        </div>
      )}
    </section>
  );
}

const panelStyle: React.CSSProperties = {
  padding: '12px 16px',
  marginBottom: 12,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
};

const cellStyle: React.CSSProperties = { padding: '2px 16px 2px 0' };
